import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import BranchUploadStep from "./BranchUploadStep";
import BranchCheckingStep from "./BranchCheckingStep";
import BranchResultStep from "./BranchResultStep";
import { CheckCircle, CloudUpload, ShieldCheck } from "react-bootstrap-icons";
import { Button, Col, Form, Row } from "react-bootstrap";
import FalconComponentCard from "components/common/FalconComponentCard";
import HorizontalStepper from "features/practitioner/components/HorizontalStepper";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { setTableData } from "../config/branchSlice";
import { GetAllPoyer } from "features/eliglibility/eligibility-check-list/config/eligibilityCheckListApi";
import { ImportBranch } from "../config/branchApi";
import UploadBranchModal from "./UploadBranchModal";
import BackButton from "components/common/custom/back-button";
import paths from "routes/paths";

const BranchImport = () => {
  const dispatch = useDispatch();
  const { poyerList } = useSelector((state) => state.eligibilityCheckList);

  const { isExcelValid, excelData, validationErrors, tableData } = useSelector(
    (state) => state.branch
  );
  const [showFileError, setShowFileError] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showUploadBranch, setShowUploadBranch] = useState(false);
  const [payload, setPayload] = useState(false);

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {
      excelFile: null,
    },
  });
  const steps = [
    {
      label: "Upload",
      icon: CloudUpload,
      component: (
        <BranchUploadStep
          control={control}
          watch={watch}
          setValue={setValue}
          errors={errors}
        />
      ),
    },
    {
      label: "Checking",
      icon: ShieldCheck,
      component: <BranchCheckingStep />,
    },
    {
      label: "Result",
      icon: CheckCircle,
      component: <BranchResultStep />,
    },
  ];
  useEffect(() => {
    if (poyerList?.length === 0) {
      dispatch(GetAllPoyer());
    }
  }, [poyerList]);

  useEffect(() => {
    const data = excelData.map((row, index) => {
      const rowNum = index + 2;
      const rowErrors = validationErrors.filter((err) => err.row === rowNum);

      return {
        ...row,
        rowNum,
        isValid: rowErrors.length === 0,
        errors: rowErrors.map((e) => e.message),
      };
    });
    dispatch(setTableData(data));
  }, [excelData, validationErrors, dispatch]);

  const validateFile = async () => {
    const isValid = await trigger("excelFile");
    setShowFileError(!isValid);
    return isValid;
  };
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleContinue = async () => {
    if (activeStep === 0) {
      const isValid = await validateFile();
      if (isValid) {
        setActiveStep(1);
        setShowFileError(false);
      } else {
        setShowFileError(true);
      }
    } else if (activeStep === steps.length - 1) {
      const providerCodeMap = {
        Bupa: "Bupa",
        Tawuniya: "Tawuniya",
        MedGulf: "MedGulf",
        Malath: "Malath",
        SAICO: "SAICO",
        SABB: "SABB",
        "Gulf Union": "Gulf Union",
        "Alrajhi Takaful": "Alrajhi Takaful",
        "Gulf Insurance Group": "Gulf Insurance Group",
        Wataniyarn: "Wataniyarn",
        Medivisarn: "Medivisarn",
        TCS: "TCS",
        Globemed: "Globemed",
        Nextcare: "Nextcare",
        "Al-Etihad": "Al-Etihad",
        MedNet: "MedNet",
      };

      const branch_data = {};

      tableData.forEach((branch, index) => {
        const provider_code_details = {};
        let providerIndex = 1;

        Object.entries(providerCodeMap).forEach(([key, payerName]) => {
          const code =
            branch?.[
              `provider_code_${key.toLowerCase().replace(/[\s-]/g, "_")}`
            ] || // e.g. provider_code_bupa
            branch?.[key]; // e.g. Bupa

          const payer = poyerList?.find(
            (p) =>
              p.payer_name?.trim().toLowerCase() === payerName.toLowerCase()
          );

          if (code && payer) {
            provider_code_details[providerIndex] = {
              provider_code: code,
              payer_id: payer.payer_id,
            };
            providerIndex++;
          }
        });

        branch_data[index + 1] = {
          branch_name:
            branch?.["Branch Name (Eng)"] || branch?.branch_name_eng || "",
          branch_arabic_name:
            branch?.["Branch Name (Arabic)"] ||
            branch?.branch_name_arabic ||
            "",
          branch_city:
            branch?.City?.value || branch?.City || branch?.city || "",
          branch_type:
            branch?.["Branch Type"] || branch?.branch_type?.value || "",
          branch_address: "",
          branch_telephone: branch?.mobile_no || branch?.["Phone No"] || "",
          branch_building_no:
            branch?.building_no || branch?.["Building No"] || "",
          branch_additional_no:
            branch?.additional_no || branch?.["Additional No"] || "",
          branch_unit_no: branch?.["Unit No"] || branch?.unit_no || "",
          branch_street_name:
            branch?.["Street Name"] || branch?.street_name || "",
          branch_district: branch?.["District"] || branch?.district || "",
          branch_zip_code: branch?.["Zip Code"] || branch?.zip_code || "",
          branch_country: branch?.country?.value || branch?.["Country"] || "",
          branch_vatno: branch?.vat_no || branch?.["VAT No"] || "",
          branch_crno: branch?.cr_no || branch?.["CR No"] || "",
          branch_cchino: branch?.cchi_no || branch?.["CHI No"] || "",
          branch_nphies_code: branch?.["Nphies ID"] || branch?.nphies_id || "",
          provider_code_details,
        };
      });

      const payload = { branch_data };
      setPayload(payload);
      // return;
      setShowUploadBranch(true);
    } else {
      // ✅ Advance to next step if not first or last
      setActiveStep((prev) => prev + 1);
    }
  };

  return (
    <>
      <Form noValidate className="p-0">
        <FalconComponentCard
          className="shadow-md"
          style={{ position: "relative" }}
        >
          <FalconComponentCard.Header
            className="d-flex fixed-header justify-content-between align-items-center"
            sx={{
              position: "sticky !important",
              top: 68,
              zIndex: 1020,
              backgroundColor: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <BackButton path={paths.branchList} title={"Branch Import"} />

            <Col xs="auto" className="d-flex gap-2">
              {activeStep > 0 && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleBack}
                >
                  Back
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="success"
                onClick={handleContinue}
                disabled={
                  (activeStep === 0 && !isExcelValid) ||
                  (activeStep === 1 && tableData.some((row) => !row.isValid))
                }
              >
                {activeStep === steps.length - 1 ? "Finish" : "Continue"}
              </Button>
            </Col>
          </FalconComponentCard.Header>
          <FalconComponentCard.Body>
            <Row>
              <Col xs={12}>
                <HorizontalStepper
                  isHorizaontal
                  steps={steps}
                  activeStep={activeStep}
                />
              </Col>
              <Col xs={12}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeStep}
                    initial={{ opacity: 0, x: -100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                  >
                    {steps[activeStep]?.component || null}
                  </motion.div>
                </AnimatePresence>
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </FalconComponentCard>
      </Form>
      <UploadBranchModal
        payload={payload}
        onClose={() => setShowUploadBranch(false)}
        show={showUploadBranch}
      />
    </>
  );
};

export default BranchImport;
